import { type CameraControls, CameraControlsImpl } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { type RefObject, useEffect } from 'react';

import { matchesInputAction } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import { MouseButton } from 'app/modules/wallforming/components/input-binding/types/mouseButton';
import {
    chordFromWheelEvent,
    formatChord,
    getEventModifiers,
    mouseCode
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { InputChord } from 'app/modules/wallforming/components/input-binding/types/inputChord';

const { ACTION } = CameraControlsImpl;

type CameraButtonName = 'left' | 'middle' | 'right';

const cameraButtonNames: Record<MouseButton, CameraButtonName> = {
    [MouseButton.Left]: 'left',
    [MouseButton.Middle]: 'middle',
    [MouseButton.Right]: 'right'
};

const cameraActions = [
    [InputActionId.CameraRotate, ACTION.ROTATE],
    [InputActionId.CameraTruck, ACTION.TRUCK],
    [InputActionId.CameraZoom, ACTION.ZOOM]
] as const;

type CameraAction = (typeof cameraActions)[number][1] | typeof ACTION.NONE | typeof ACTION.DOLLY;

/** Placement mapping; the real action is assigned per press from the active bindings. Mutated by the controls, so one per instance. */
export const createCameraMouseButtons = () => ({
    left: ACTION.NONE,
    middle: ACTION.NONE,
    right: ACTION.NONE,
    wheel: ACTION.NONE
});

interface CameraControlsEventsProps {
    cameraControlsRef: RefObject<CameraControls | null>;
    isMainCamera: boolean;
}

/** Applies the camera gesture bindings (including modifiers) to the controls right before they read their buttons. */
export const useCameraControlsEvents = ({ cameraControlsRef, isMainCamera }: CameraControlsEventsProps) => {
    const gl = useThree((state) => state.gl);

    useEffect(() => {
        const activeButtons = new Set<MouseButton>();

        const resolveAction = (chord: InputChord, controls: CameraControls): CameraAction => {
            const state = usePlacementStore.getState();
            const isRotationLocked = isMainCamera && state.isMainCameraLocked;
            const action =
                cameraActions.find(
                    ([actionId, cameraAction]) =>
                        !(isRotationLocked && cameraAction === ACTION.ROTATE) &&
                        matchesInputAction(actionId, chord, state)
                )?.[1] ?? ACTION.NONE;
            return action === ACTION.ZOOM && controls.camera.type === 'PerspectiveCamera' ? ACTION.DOLLY : action;
        };

        const applyButton = (button: MouseButton, event: MouseEvent | KeyboardEvent) => {
            const controls = cameraControlsRef.current;
            if (controls) {
                const chord = formatChord({
                    modifiers: getEventModifiers(event),
                    code: mouseCode(button),
                    trigger: InputTrigger.Drag
                });
                controls.mouseButtons[cameraButtonNames[button]] = resolveAction(chord, controls);
            }
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.target === gl.domElement && event.button in cameraButtonNames) {
                activeButtons.add(event.button);
                applyButton(event.button, event);
            }
        };

        const handlePointerUp = (event: PointerEvent) => {
            if (activeButtons.delete(event.button) && cameraControlsRef.current) {
                cameraControlsRef.current.mouseButtons[cameraButtonNames[event.button as MouseButton]] = ACTION.NONE;
            }
        };

        /** Re-evaluates held buttons so modifiers can be pressed or released mid-drag. */
        const handleModifierChange = (event: PointerEvent | KeyboardEvent) =>
            activeButtons.forEach((button) => applyButton(button, event));

        const handleWheel = (event: WheelEvent) => {
            const controls = cameraControlsRef.current;
            if (controls && event.target === gl.domElement) {
                controls.mouseButtons.wheel = resolveAction(chordFromWheelEvent(event), controls);
            }
        };

        window.addEventListener('pointerdown', handlePointerDown, true);
        window.addEventListener('pointerup', handlePointerUp, true);
        window.addEventListener('pointermove', handleModifierChange, true);
        window.addEventListener('keydown', handleModifierChange, true);
        window.addEventListener('keyup', handleModifierChange, true);
        window.addEventListener('wheel', handleWheel, { capture: true, passive: true });
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, true);
            window.removeEventListener('pointerup', handlePointerUp, true);
            window.removeEventListener('pointermove', handleModifierChange, true);
            window.removeEventListener('keydown', handleModifierChange, true);
            window.removeEventListener('keyup', handleModifierChange, true);
            window.removeEventListener('wheel', handleWheel, true);
        };
    }, [cameraControlsRef, gl.domElement, isMainCamera]);
};
